/**
 * Publishes the header's height to CSS, as `--header-height`.
 *
 * The header is sticky, so it covers the top of the page -- and the browser
 * scrolls an `#anchor` to the very top of the viewport, which is now underneath
 * it. `scroll-padding-top` is the fix, and the number it needs is the header's
 * height: a heading scrolled to 0 would otherwise start its life hidden behind
 * the wordmark.
 *
 * The stylesheet carries that number as a fallback per breakpoint, measured in
 * a browser, and those two values are the whole reason this file exists: they
 * are right until somebody adds a line to the header, and then they are wrong
 * in the direction that hides content. This measures the element instead, so
 * the offset follows whatever the header actually became -- a second row of
 * navigation, a bigger wordmark, a font that loaded late and changed the
 * tagline's line box.
 *
 * Both headers are looked for by name rather than taking the first `<header>`:
 * the static front page has `.site-header` and every application screen has
 * `.site-nav`, and no page has both.
 *
 * Progressive enhancement like the rest of the bundle. Without this the
 * stylesheet's fallback applies, which is close enough that an anchor lands
 * under its heading rather than under the header.
 */
export function headerHeight(root = document) {
  const header = root.querySelector(".site-header, .site-nav");

  // Five screens have no header: the sign-in, registration, password-reset and
  // check-your-inbox pages define no `chrome` block at all. They still get the
  // stylesheet's fallback, which is the height of a header they do not have --
  // 152px of empty space above whatever an anchor or a focused field scrolls
  // to, on the screens where a soft keyboard is doing the scrolling. Saying
  // zero is the measurement, not a special case: the header is this tall.
  if (!header) {
    document.documentElement.style.setProperty("--header-height", "0px");
    return;
  }

  const publish = () => {
    // Rounded up: a fractional device pixel short is a hairline of the
    // target's first line still under the header, and a pixel of extra room
    // above it is invisible.
    const height = Math.ceil(header.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--header-height", `${height}px`);
  };

  publish();

  // The height changes with the viewport (one breakpoint, and the header wraps
  // below it), and with the content -- the mobile panel opening, a late font.
  // A ResizeObserver sees all of those; a resize listener sees only the first,
  // so it is the fallback and not the implementation.
  if (typeof ResizeObserver === "function") {
    // The border box, because that is what `publish` measures. The default is
    // the content box, which would miss a change to the header's padding or
    // its rule -- the two things most likely to be edited next, and neither of
    // them visible to an observer watching the content. It fires today only
    // because the padding happens to change at the same breakpoint the
    // wordmark does.
    new ResizeObserver(publish).observe(header, { box: "border-box" });
  } else {
    window.addEventListener("resize", publish);
  }
}
